// client/src/pages/PTO/AdminCoveragePanel.tsx
// Read-only: the two admins on duty this week for the Admin Team email inbox, the
// fax inbox, and saving SOWs to M-Files, plus what the pair covers. Weekly rotation
// seeded from the Admin Schedule; the schedule is fixed, so this view is display-only.

import React, { useMemo } from 'react';
import type { AdminDutyAssignment, PTOEmployee } from './ptoTypes';
import { formatDate, getOfficeColor } from './ptoUtils';

// ─── Props ──────────────────────────────────────────────────────────────────

interface AdminCoveragePanelProps {
  adminDuty: AdminDutyAssignment[];
  employees: PTOEmployee[];
  currentWeek: Date; // Monday of the displayed week
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function mondayIso(monday: Date): string {
  const y = monday.getFullYear();
  const m = String(monday.getMonth() + 1).padStart(2, '0');
  const d = String(monday.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// ─── Component ──────────────────────────────────────────────────────────────

const AdminCoveragePanel: React.FC<AdminCoveragePanelProps> = ({ adminDuty, employees, currentWeek }) => {
  const weekStart = useMemo(() => mondayIso(currentWeek), [currentWeek]);

  const empMap = useMemo(() => {
    const m = new Map<string, PTOEmployee>();
    for (const e of employees) m.set(e.staffIndex, e);
    return m;
  }, [employees]);

  const weekAdmins = useMemo(
    () =>
      adminDuty
        .filter((a) => a.weekStart === weekStart)
        .sort((a, b) => a.sortOrder - b.sortOrder),
    [adminDuty, weekStart],
  );

  return (
    <div>
      <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 10 }}>
        Week of {formatDate(weekStart)}
      </div>

      {weekAdmins.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--muted)', fontStyle: 'italic', padding: '4px 0 10px' }}>
          No admins scheduled this week
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
          {weekAdmins.map((a) => {
            const emp = empMap.get(a.staffIndex);
            const officeColor = emp ? getOfficeColor(emp.office) : '#8090a0';
            return (
              <div
                key={a.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '9px 11px',
                  borderRadius: 8,
                  border: '1px solid var(--border)',
                  borderLeft: `3px solid ${officeColor}`,
                  background: 'rgba(255,255,255,.03)',
                }}
              >
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
                  {emp?.displayName ?? a.staffIndex}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminCoveragePanel;
